import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {faBook, faDownload, faFolderOpen, faGraduationCap} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import LearnerShell from "../components/learner/LearnerShell";
import {imageUrl, money, shortDate} from "../lib/format";
import {useUserLearning} from "../store/user";

const MATERIAL_PATH = {
  BOOK: "books", JOURNAL: "journals", PROJECT_TOPIC: "project-topics",
  SEMINAR_TOPIC: "seminar-topics", RESEARCH_MATERIAL: "research-materials", MANUAL: "manuals",
};

const Library = () => {
  const navigate = useNavigate();
  const {
    myLibraryResources, myLibraryCourses, myLibraryLoading, myLibraryError,
    fetchMyLibrary, downloadMyLibraryResource,
  } = useUserLearning();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [busyKey, setBusyKey] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => { fetchMyLibrary(); }, [fetchMyLibrary]);

  const resources = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (myLibraryResources || []).filter((item) =>
      (filter === "ALL" || item.itemType === filter) &&
      (!q || `${item.title} ${item.itemType} ${item.fileFormat || ""}`.toLowerCase().includes(q))
    );
  }, [myLibraryResources, search, filter]);
  const courses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (myLibraryCourses || []).filter((item) =>
      (filter === "ALL" || filter === "COURSE") && (!q || item.title?.toLowerCase().includes(q))
    );
  }, [myLibraryCourses, search, filter]);

  const download = async (item) => {
    const key = `${item.itemType}:${item.itemId}`;
    setBusyKey(key);
    setNotice("");
    const result = await downloadMyLibraryResource(item.itemType, item.itemId);
    setBusyKey("");
    if (!result.success || !result.data?.fileUrl) {
      setNotice(result.error || "A download link is not available for this resource.");
      return;
    }
    window.location.assign(result.data.fileUrl);
  };

  return (
    <LearnerShell
      title="My Library"
      subtitle="Your purchased resources and enrolled courses, together in one place."
      icon={faFolderOpen}
      actions={
        <>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search your library" aria-label="Search your library" className="rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground" />
          <select value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Filter library" className="rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground">
            <option value="ALL">All items</option>
            <option value="COURSE">Courses</option>
            <option value="BOOK">Books</option>
            <option value="JOURNAL">Journals</option>
            <option value="PROJECT_TOPIC">Project topics</option>
            <option value="SEMINAR_TOPIC">Seminar topics</option>
            <option value="RESEARCH_MATERIAL">Research materials</option>
            <option value="MANUAL">Manuals and guides</option>
          </select>
        </>
      }
    >
      {notice ? <p role="status" className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{notice}</p> : null}
      {myLibraryError ? <p role="alert" className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{myLibraryError}</p> : null}
      {myLibraryLoading && resources.length === 0 && courses.length === 0 ? (
        <div className="space-y-3">{[0, 1, 2].map((n) => <div key={n} className="h-24 animate-pulse rounded-xl bg-muted" />)}</div>
      ) : resources.length || courses.length ? (
        <div className="space-y-3">
          {courses.map((course) => (
            <article key={`COURSE:${course.itemId}`} className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center">
              {course.thumbnailUrl ? <img src={imageUrl(course.thumbnailUrl)} alt="" className="h-20 w-full rounded-lg object-cover sm:w-28" /> : <span className="flex h-20 w-full items-center justify-center rounded-lg bg-muted text-muted-foreground sm:w-28"><FontAwesomeIcon icon={faGraduationCap} /></span>}
              <div className="min-w-0 flex-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-blue-800">Course</span>
                <h2 className="m-0 mt-1 text-base font-semibold text-foreground">{course.title}</h2>
                <p className="m-0 mt-1 text-xs text-muted-foreground">{course.resourcePerson?.name || "Instructor"} · Enrolled {shortDate(course.enrolledAt)}</p>
              </div>
              <button type="button" onClick={() => navigate("/my-courses")} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Continue learning</button>
            </article>
          ))}
          {resources.map((item) => {
            const key = `${item.itemType}:${item.itemId}`;
            const path = MATERIAL_PATH[item.itemType];
            return (
              <article key={key} className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center">
                {item.thumbnailUrl ? <img src={imageUrl(item.thumbnailUrl)} alt="" className="h-20 w-full rounded-lg object-cover sm:w-28" /> : <span className="flex h-20 w-full items-center justify-center rounded-lg bg-muted text-muted-foreground sm:w-28"><FontAwesomeIcon icon={faBook} /></span>}
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-blue-800">{item.itemType.replaceAll("_", " ")}</span>
                  <h2 className="m-0 mt-1 text-base font-semibold text-foreground">{item.title}</h2>
                  <p className="m-0 mt-1 text-xs text-muted-foreground">Purchased {shortDate(item.purchasedAt)}{item.fileFormat ? ` · ${item.fileFormat}` : ""}{item.price != null ? ` · ${money(item.price)}` : ""}</p>
                  {item.accessRevoked ? <p className="m-0 mt-1 text-xs font-medium text-red-700">Access revoked</p> : null}
                </div>
                <div className="flex shrink-0 gap-2">
                  {path ? <button type="button" onClick={() => navigate(`/materials/${path}/${encodeURIComponent(item.slug || item.itemId)}`)} className="rounded-md border border-border px-3 py-2 text-sm font-semibold text-foreground">Details</button> : null}
                  {item.downloadable && !item.accessRevoked ? <button type="button" disabled={busyKey === key} onClick={() => download(item)} className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"><FontAwesomeIcon icon={faDownload} />{busyKey === key ? "Preparing…" : "Download"}</button> : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-card px-5 py-16 text-center">
          <FontAwesomeIcon icon={faFolderOpen} className="text-3xl text-muted-foreground" />
          <h2 className="mt-3 text-lg font-semibold text-foreground">Nothing here yet</h2>
          <p className="mt-1 text-sm text-muted-foreground">Purchased resources and enrolled courses will appear here.</p>
          <button type="button" onClick={() => navigate("/")} className="mt-5 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">Explore Fillop</button>
        </div>
      )}
    </LearnerShell>
  );
};

export default Library;
